'use client';

/**
 * The New-Home widget registry + default layout.
 *
 * A widget is a self-contained unit — an id and a Component that fetches its own
 * data. The layout is a separate ordered list of `{ id, colSpan }`, so widgets
 * are MOVABLE: reordering (or later drag-and-drop + persistence) only rewrites
 * this list, never a widget. `hero: true` marks the cards that ride the mobile
 * "Panorama" swipe carousel.
 */
import type { ComponentType } from 'react';

import {
  AssinaturasWidget,
  CategoriasWidget,
  DisponivelWidget,
  EmAbertoWidget,
  MetasWidget,
  PatrimonioWidget,
  RevisarWidget,
  RitmoWidget,
  UltimasWidget,
} from './widgets';

export type WidgetId =
  | 'disponivel'
  | 'ritmo'
  | 'em-aberto'
  | 'patrimonio'
  | 'metas'
  | 'revisar'
  | 'categorias'
  | 'assinaturas'
  | 'ultimas';

export interface WidgetDefinition {
  id: WidgetId;
  Component: ComponentType;
  /** Rides the mobile Panorama swipe carousel (top hero row). */
  hero: boolean;
}

export const WIDGETS: Record<WidgetId, WidgetDefinition> = {
  disponivel: { id: 'disponivel', Component: DisponivelWidget, hero: true },
  ritmo: { id: 'ritmo', Component: RitmoWidget, hero: true },
  'em-aberto': { id: 'em-aberto', Component: EmAbertoWidget, hero: true },
  patrimonio: { id: 'patrimonio', Component: PatrimonioWidget, hero: true },
  metas: { id: 'metas', Component: MetasWidget, hero: true },
  revisar: { id: 'revisar', Component: RevisarWidget, hero: false },
  categorias: { id: 'categorias', Component: CategoriasWidget, hero: false },
  assinaturas: { id: 'assinaturas', Component: AssinaturasWidget, hero: false },
  ultimas: { id: 'ultimas', Component: UltimasWidget, hero: false },
};

/** One cell on the desktop 6-column grid. `colSpan` is out of 6. */
export interface WidgetPlacement {
  id: WidgetId;
  colSpan: 2 | 3 | 4 | 6;
}

/**
 * Default desktop layout — mirrors the design:
 *   row 1  Disponível (4) · Ritmo (2)
 *   row 2  Em aberto (2) · Patrimônio (2) · Metas (2)
 *   row 3  Revisar (3) · Top categorias (3)
 *   row 4  Assinaturas (3) · Últimas (3)
 */
export const DEFAULT_LAYOUT: WidgetPlacement[] = [
  { id: 'disponivel', colSpan: 4 },
  { id: 'ritmo', colSpan: 2 },
  { id: 'em-aberto', colSpan: 2 },
  { id: 'patrimonio', colSpan: 2 },
  { id: 'metas', colSpan: 2 },
  { id: 'revisar', colSpan: 3 },
  { id: 'ultimas', colSpan: 3 },
  { id: 'assinaturas', colSpan: 3 },
  { id: 'categorias', colSpan: 3 },
];
